<template>
  <v-container id="product-feed-drawer">
    <div class="pl-6 pr-2 py-2">
      <h3 class="pr-3 my-2 d-flex justify-space-between">
        {{ drawerHeader.title }}
        <v-btn
          id="close-product-feed-drawer"
          v-track="'close-product-feed-drawer'"
          icon
          small
          class="ma-0 pa-0"
          @click.stop="$store.commit('ui/drawer.right.toggle')"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </h3>
      <v-container class="pl-0">
        <div
          class="body-1 mb-7"
        >
          <a
            v-track="'product-feed-read-more'"
            href="https://galaxy.maropost.com/s/article/Product-Feeds"
            target="_blank"
          >Want to learn more about Product Feeds?</a>
        </div>
        <v-form
          ref="feedForm"
          v-model="valid"
          lazy-validation
        >
          <v-row>
            <v-col
              cols="12"
              class="pb-0"
            >
              <v-text-field
                id="product-feed-name"
                v-model.trim="productFeed.name"
                label="Product Feed Name *"
                :rules="[
                  v => !!v || 'Name is required',
                  v => !v || !!v.trim() || 'Name cannot be blank!',
                ]"
                outlined
                required
                :error-messages="errorBag.name"
                persistent-hint
                @input="errorBag.name = ''"
              />
            </v-col>
            <v-col
              cols="12"
              class="py-0 mt-n2"
            >
              <v-switch
                v-model="productFeed.filters.only_active"
                v-track="'only-active-product'"
                dense
                color="#03B6FC"
                label="Active products only"
              />
            </v-col>
            <v-col
              cols="12"
              class="py-0 mt-n4"
            >
              <v-switch
                v-model="productFeed.filters.only_in_stock"
                v-track="'only-in-stock-product'"
                dense
                color="#03B6FC"
                label="In Stock products only"
              />
            </v-col>
            <v-col
              cols="12"
              class="py-0 mt-n4"
            >
              <v-switch
                v-model="productFeed.filters.only_published"
                v-track="'only-published-product'"
                dense
                color="#03B6FC"
                label="Approved for Webstore products only"
              />
            </v-col>
            <v-col
              cols="12"
              class="pb-0"
            >
              <v-select
                v-model="productFeed.filters.source"
                v-track="'filter-source'"
                class="ml-1"
                :items="sources"
                required
                attach
                :item-text="item => (item.label === 'Commerce') ? `${item.label} Cloud` : item.label"
                item-value="id"
                label="Source *"
                :loading="sourcesLoader"
                :rules="[
                  v => v.length > 0 || 'Source is required',
                ]"
                outlined
                item-color="rgba(0,0,0,0.87)"
                :menu-props="{ offsetY: true }"
                @input="onChangeSource()"
              />
            </v-col>
            <v-col
              cols="12"
              class="pb-0"
              v-if="productFeed.filters.source != 'default' && featureFlag"
            >
              <v-select
                v-model="productFeed.filters.store_id"
                v-track="'filter-source'"
                class="ml-1"
                :items="stores"
                required
                attach
                :item-text="item => item.name"
                item-value="id"
                label="Store Name *"
                :loading="storesLoader"
                :rules="touched.store ? storeRules : []"
                outlined
                item-color="rgba(0,0,0,0.87)"
                :menu-props="{ offsetY: true }"
                @input="onChangeStore()"
                @blur="markTouched('store')"
              />
            </v-col>
            <v-col
              cols="12"
            >
              <v-autocomplete
                v-model="productFeed.filters.brands"
                class="custom-selectbox ml-1 custom-height background-white"
                attach
                clearable
                outlined
                :items="brands"
                :loading="brandLoader"
                small-chips
                label="Brand"
                :item-text="item => item"
                multiple
                item-color="#03B6FC"
                return-object
                persistent-hint
                :disabled="featureFlag ? isBrandSelectionDisabled : false"
                no-data-text="No Brands are available for the selected Source"
              >
                <template v-slot:selection="data">
                  <v-chip
                    chip-icon
                    v-bind="data.attrs"
                    :input-value="data.selected"
                    color="rgba(0,107,175,0.2)"
                  >
                    <span class="pr-1 overflow-hidden text-truncate">
                      {{ data.item }}
                    </span>
                    <v-icon
                      small
                      @click="removeBrand(data.item)"
                    >
                      mdi-close-circle
                    </v-icon>
                  </v-chip>
                </template>
                <template #item="{ item, attrs, on }">
                  <v-list-item
                    v-bind="attrs"
                    #default="{ active }"
                    v-on="on"
                  >
                    <v-list-item-action>
                      <v-checkbox
                        color="#03b6fc"
                        :input-value="active"
                      />
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ item }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>
          <v-divider />
          <v-subheader class="pa-0">
            FILTER CATEGORIES
          </v-subheader>
          <v-row class="mt-n2">
            <v-radio-group
              id="category-filter-on-feed"
              v-model="categoryFilter"
              v-track="'category-filter-on-feed'"
              class="ma-0 w-100"
              @change="resetFilter()"
            >
              <v-col
                v-for="(cat,catIndex) in categoriesFilter"
                :key="catIndex"
                class="py-1"
              >
                <v-radio
                  :id="`category-filter-option-${catIndex}`"
                  v-track="`category-filter-option-${catIndex}`"
                  :value="cat.value"
                  class="pa-0"
                  color="#03b6fc"
                  :label="cat.text"
                />
                <div
                  v-if="cat.value === categoryFilter && categoryFilter !== ' '"
                  class="py-3"
                >
                  <div
                    class="categories-fieldset mt-n2 ml-2 px-1"
                    :class="{'d-none' : (!categoryFocusFlag && categoryCountShow == 0)}"
                  >
                    <label class="v-label--active theme--light">
                      Categories *
                    </label>
                  </div>
                  <treeselect
                    v-if="categoryFilter === 'limit'"
                    v-model="productFeed.filters.included_categories"
                    :multiple="true"
                    :options="categories"
                    value-consists-of="ALL"
                    :show-count="true"
                    show-count-of="ALL_DESCENDANTS"
                    :close-on-select="true"
                    :limit="10"
                    :normalizer="normalizer"
                    value-format="id"
                    no-results-text="Category not found."
                    no-options-text="No Categories are available for the selected Source"
                    placeholder="Categories *"
                    class="pr-2 background-white"
                    @open="onOpenTags()"
                    @close="onCloseTags()"
                  />
                  <treeselect
                    v-if="categoryFilter === 'exclude'"
                    v-model="productFeed.filters.excluded_categories"
                    :multiple="true"
                    :options="categories"
                    value-consists-of="ALL"
                    :show-count="true"
                    show-count-of="ALL_DESCENDANTS"
                    :close-on-select="true"
                    :limit="10"
                    :normalizer="normalizer"
                    value-format="id"
                    no-results-text="Category not found"
                    no-options-text="No Categories are available for the selected Source"
                    placeholder="Categories *"
                    class="pr-2 background-white"
                    @open="onOpenTags()"
                    @close="onCloseTags()"
                  />
                </div>
              </v-col>
            </v-radio-group>
          </v-row>
          <v-divider />
          <v-subheader class="pa-0">
            <span class="mr-2">RECOMMENDATIONS TYPE</span>
          </v-subheader>
          <v-row class="mt-n2">
            <v-col>
            <v-select
                  id="'metrics-options'"
                  v-track="`metric-option-${productFeed.metric}`"
                  v-model="productFeed.metric"
                  class="ml-1 mb-2"
                  :items="metricsOption"
                  required
                  attach
                  item-text="text"
                  item-value="value"
                  outlined
                  item-color="rgba(0,0,0,0.87)"
                  :menu-props="{ offsetY: true }"
                  @change="resetDays()"
                  hide-details
                />
              <v-col
                v-for="(metric,metricIndex) in metricsOption"
                :key="metricIndex"
                class="py-0 px-0"
              >
                <v-select
                  v-if="metric.value === productFeed.metric && productFeed.metric === 'best_sellers'"
                  :id="`metric-best-seller-days-list`"
                  v-model="productFeed.filters.day_range"
                  v-track="`metric-best-seller-days-list`"
                  class="ml-1"
                  :items="metricsBestSellerDays"
                  required
                  attach
                  item-text="text"
                  item-value="value"
                  outlined
                  item-color="rgba(0,0,0,0.87)"
                  :menu-props="{ offsetY: true }"
                  hide-details
                />
                <v-select
                  v-if="metric.value === productFeed.metric && metric.value ==='new_arrivals'"
                  :id="`metric-new-arrivals-list`"
                  v-model="productFeed.filters.day_range"
                  v-track="`metric-new-arrivals-list`"
                  class="ml-1"
                  :items="metricsOtherDays"
                  required
                  attach
                  item-text="text"
                  item-value="value"
                  outlined
                  item-color="rgba(0,0,0,0.87)"
                  :menu-props="{ offsetY: true }"
                  hide-details
                />
                <v-select
                  v-if="metric.value === productFeed.metric && metric.value ==='trending'"
                  :id="`metric-top-trending-list`"
                  v-model="productFeed.filters.day_range"
                  v-track="`metric-top-trending-list`"
                  class="ml-1"
                  :items="metricsOtherDays"
                  required
                  attach
                  item-text="text"
                  item-value="value"
                  outlined
                  item-color="rgba(0,0,0,0.87)"
                  :menu-props="{ offsetY: true }"
                  hide-details
                />
              </v-col>
            </v-col>
          </v-row>
  <div v-if="isPurchasedTogether">
    <v-row>
      <span class="ml-3">Please choose which products would generate recommendations</span>
      <v-radio-group
        v-model="recommendationType"
        class="ml-3"
        row
        @change="handleFeedChange"
      >
        <v-radio
          v-for="product in productOptions"
          :key="product.value"
          :label="product.text"
          :value="product.value"
          color="mpBlue"
        />
        <v-tooltip :color="uiConstant.tooltipColor" bottom>
          <template v-slot:activator="{ on }">
            <v-icon class="material-icons ml-1" v-on="on">info_outline</v-icon>
          </template>
          <span>
            Recommendations can be based on specific product SKUs<br/>
            or on the products displayed in a campaign<br/> 
            (i.e. abandoned cart items)
          </span>
        </v-tooltip>
      </v-radio-group>
    </v-row>

    <v-row>
      <!-- Product Feeds based on recommendationType -->
      <v-col cols="12" class="px-1 py-0" v-if="isFeeds">
        <v-select
          :id="`tag-type-list`"
          v-model="productFeed.filters.tag_type"
          v-track="`tag-type-list`"
          class="mx-4 mb-1"
          :items="productFeedOptions()"
          label="Select Feed Type"
          :rules="feedRules"
          required
          attach
          item-text="text"
          item-value="value"
          outlined
          item-color="rgba(0,0,0,0.87)"
          :menu-props="{ offsetY: true }"
        />
      </v-col>
      <!-- Custom Dropdown for Product SKUs -->
      <v-col cols="12" class="px-1 py-0" v-else>
        <custom-dropdown
          ref="customDropdown"
          :type="'product'"
          :label="'Product SKU'"
          class="mb-1 mx-4 w-100"
          :lists="dataObj"
          :abPayload="{source:productFeed.filters.source,source_id:productFeed.filters.store_id}"
          :items.sync="skuProducts"
          :hideInfoText="true"
          :hideWarning="true"
          :rules="listRules"
          :allowSelectAll="false"
          :abPayloadInSearch="true"
          :menuProps="{ maxHeight:250 }"
        />
      </v-col>
    </v-row>
    <!-- Select Feed based on tag_type -->
    <v-row v-if="showFeedList">
      <v-col cols="12" class="px-1 py-0">
        <v-select
          :id="`feed-list`"
          v-model="productFeed.filters.selected_feed_id"
          v-track="`feed-list`"
          :label="productFeed.filters.tag_type == 'abandoned_carts'?'Select Abandoned Tag':'Select Product Feed'"
          class="mx-4 mb-2"
          :items="feedList"
          required
          attach
          item-text="name"
          item-value="id"
          outlined
          item-color="rgba(0,0,0,0.87)"
          :menu-props="{ offsetY: true }"
          :rules="feedRules"
        />
     </v-col>
    </v-row>
  </div>

          <v-divider />
          <v-subheader class="pa-0">
            SORT BY
          </v-subheader>
          <v-row class="mt-n2">
            <v-radio-group
              id="feed-sort-by-filter"
              v-model="productFeed.filters.order"
              v-track="'feed-sort-by-filter'"
              class="ma-0"
            >
              <v-col
                v-for="(sortBy,sortByIndex) in sortByFilter"
                :key="sortByIndex"
                class="py-1"
              >
                <v-radio
                  :id="`sort-by-option-${sortByIndex}`"
                  v-track="`sort-by-option-${sortByIndex}`"
                  :value="sortBy.value"
                  class="pa-0"
                  color="#03b6fc"
                  :label="sortBy.text"
                />
              </v-col>
            </v-radio-group>
          </v-row>
          <v-divider />
          <v-row>
            <v-col cols="12 pl-0 mt-2">
              <v-btn
                id="product-feed-drawer-cancel"
                v-track="'product-feed-drawer-cancel'"
                outlined
                class="ml-3 px-8"
                @click.stop="$store.commit('ui/drawer.right.toggle')"
              >
                cancel
              </v-btn>
              <v-btn
                id="product-feed-submit"
                v-track="'product-feed-submit'"
                class="ml-3 px-8"
                :dark="valid"
                :disabled="!valid"
                @click="onSubmit()"
              >
                {{ id ? 'Save' : 'Create' }}
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-container>
    </div>
    <v-overlay :value="loader">
      <v-progress-circular
        indeterminate
        size="64"
      />
    </v-overlay>
  </v-container>
</template>
<script>
  import { $productFeeds, $crms, $products, $categories, $integrations } from '@/services/'
  import Treeselect from '@riophae/vue-treeselect'
  import '@riophae/vue-treeselect/dist/vue-treeselect.css'
  import constants from "@/constants.js";
  import CustomDropdown from "@/components/ui/CustomDropdown.vue";
  import { getSplitKey } from '@/splitClient';

  export default {
    components: {
      Treeselect,
      CustomDropdown
    },
    data () {
      return {
        valid: false,
        errorBag: {
          name: '',
          categories: '',
        },
        categoryFilter: ' ',
        categoriesFilter: [
          { value: ' ', text: 'Show all categories' },
          { value: 'limit', text: 'Limit to specific categories' },
          { value: 'exclude', text: 'Exclude specific categories' },
        ],
        categories: [],
        sortByFilter: [
          { value: ' ', text: 'Random' },
          { value: 'ASC', text: 'Price Low to High' },
          { value: 'DESC', text: 'Price High to Low' },
        ],
        metricsBestSellerDays: [
          { value: 5, text: 'Last 5 days' },
          { value: 10, text: 'Last 10 days' },
          { value: 15, text: 'Last 15 days' },
          { value: 30, text: 'Last 30 days' },
          { value: 45, text: 'Last 45 days' },
          { value: 60, text: 'Last 60 days' },
        ],
        metricsOtherDays: [
          { value: 7, text: 'Last 7 days' },
          { value: 15, text: 'Last 15 days' },
          { value: 30, text: 'Last 30 days' },
        ],
        productOptions:[
          { value:'feeds', text:'Product Feeds' },
          { value:'skus', text:'Product SKUs' }
        ],
        dataObj:{
          ids:[],
          select_all:false
        },
        listRules: [v => !!v.length || "Product SKUs are required."],
        feedRules: [value => !!value || "Feed is required."],
        skuProducts:[],
        feedList:[],
        recommendationType:'feeds',
        loader: false,
        freeFindifyRecommendations:true,
        paidFindifyRecommendations:true,
        productFeed: {
          name: '',
          metric: 'best_sellers',
          filters: {
            day_range: 5,
            included_categories: [],
            excluded_categories: [],
            order_by: 'price',
            order: ' ',
            source: '',
            store_id:'',
            only_published: true,
            only_active: true,
            only_in_stock: true,
            brands: [],
            tag_type:'',
            selected_feed_id:null,
            product_skus:[],
          },
        },
        categoriesLoader: false,
        sources: [],
        sourcesLoader: false,
        stores:[],
        storesLoader: false,
        brands: [],
        brandLoader: false,
        normalizer (node) {
          let childrenNode = ''
          if (node.children) {
            if (node.children.length) {
              childrenNode = node.children
            }
          }
          return {
            id: node.id,
            label: node.name,
            children: childrenNode,
          }
        },
        categoryFocusFlag: false,
        isDataFetched: false,
        featureFlag: null,
        allOption: [],
        oldOption: [],
        enableAllOption: false,
        touched: {
          store: false
        },
      }
    },
    computed: {
      // Handle drawer header area according to case!
      drawerHeader () {
        switch (this.action) {
          case 'new' :
            return {
              title: 'New Product Feed',
              description: '',
            }
          case 'edit' :
            return {
              title: 'Edit Product Feed',
              description: '',
            }
          default:
            return {
              title: '',
              description: '',
            }
        }
      },
      // Receive user action
      action: {
        get () { return this.$store.state.ui.drawer.right.action },
        set (value) { this.$store.state.ui.drawer.right.action = value },
      },
      id: {
        get () { return this.$store.state.ui.drawer.right.id },
        set (value) { this.$store.state.ui.drawer.right.id = value },
      },
      /**
       * showing Campaign Tags count on tag UI label
       */
      categoryCountShow () {
        let categoriesCount = 0
        if (this.productFeed.filters.included_categories.length > 0) {
          categoriesCount = this.productFeed.filters.included_categories
        } else {
          categoriesCount = this.productFeed.filters.excluded_categories
        }
        return categoriesCount.length
      },
      uiConstant() {
        return constants.FORM_BUILDER_LAYOUT;
      },
      isPurchasedTogether() {
        return this.productFeed.metric === 'purchased_together';
      },
      isFeeds() {
        return this.recommendationType === 'feeds';
      },
      showFeedList() {
        return this.isFeeds && !!this.productFeed.filters.tag_type && this.productFeed.filters.tag_type !== 'product_skus' ;
      },
      isBrandSelectionDisabled() {
        // Disable brand selection if store is not selected, except if source is "default" and no store
        return (
          this.productFeed.filters.source !== 'default' &&
          !this.productFeed.filters.store_id
        )
      },
      storeRules() {
        // Store field is required when the source is NOT "default"
        return [
          v => {
            if (this.productFeed.filters.source !== 'default') {
              return !!v || 'Store is required';
            }
            return true; // No validation if the source is 'default'
          },
        ];
      },
      metricsOption(){
        this.allOption = [
          { value: 'best_sellers', text: 'Best Sellers' },
          { value: 'new_arrivals', text: 'New Arrivals' },
          { value: 'trending', text: 'Top Trending' },
          { value: 'purchased_together', text: 'Bought Together (New!)', disabled: this.freeFindifyRecommendations },
          { value: 'similar_products', text: 'Similar Products (Coming Soon)', disabled: this.paidFindifyRecommendations },
          { value: 'people_who_viewed_also_bought', text: 'People who viewed also bought (Coming Soon)', disabled: this.paidFindifyRecommendations },
          { value: 'you_may_like_this', text: 'You may like this (Coming Soon)', disabled: this.paidFindifyRecommendations }
        ];
        this.oldOption =[
          { value: 'best_sellers', text: 'Best Sellers' },
          { value: 'new_arrivals', text: 'New Arrivals' },
          { value: 'trending', text: 'Top Trending' },
        ]
        return this.featureFlag ? 
        (this.enableAllOption ? this.allOption : this.oldOption) 
        : this.oldOption;
      }
    },
    watch: {
      recommendationType(val) {
        if (val === 'skus') {
          this.$set(this.productFeed.filters, 'tag_type', 'product_skus');
          this.$set(this.productFeed.filters, 'selected_feed_id', null);
        }
      },
      'productFeed.filters.tag_type'(newVal) {
        this.getFeeds(newVal);
      },
      dataObj() {
        this.productFeed.filters.product_skus = [...this.dataObj.ids];
      },
      stores(newStores) {
        if (newStores.length === 1) {
          this.productFeed.filters.store_id = newStores[0].id;
        }else if(!this.id){
          this.productFeed.filters.store_id = null;
        }
      },
      'productFeed.filters.source': function(newVal) {
        if(newVal === 'shopify' || newVal === 'neto' || newVal === 'keap' || newVal === 'retail_express')
          this.enableAllOption = true;
        else
          this.enableAllOption = false;
        this.handleFilterChange('source');
      },
      'productFeed.filters.store_id'(newVal){
        if(this.isDataFetched){
          this.handleFilterChange('store_id');
          this.onChangeStore()
        }
        this.getStoreFindifyStatus(newVal)
      },
      'productFeed.filters.brands': function (newVal, oldVal) {
        if (this.isDataFetched && newVal !== oldVal) {
          // Using nextTick to delay handleFilterChange until the DOM updates fully
          this.$nextTick(() => this.handleFilterChange('brands'));
        }
      },
      'productFeed.metric'(){
        if(this.isDataFetched){
          this.handleFeedChange()
        }
      }
    },
    beforeMount () { this.initData() },
    methods: {
      /**
       * @description Component initial data injection
       * according to cases
       */
      initData () {
        this.loader = true
        this.sourcesLoader = true

        this.initializeSplitClient();
        
        switch (this.action) {
          case 'new':
            $crms.fetch().then(response => {
              const results = response.data.data.types
              const sources = []
              results.forEach(source => {
                sources.push({
                  id: source,
                  label: source.toLowerCase().replace(/_/g, ' ')
                    .replace(/(?: |\b)(\w)/g, (key, p1) => {
                      return key.toUpperCase()
                    }),
                })
              })
              this.sources = sources
            }).finally(() => {
              this.sourcesLoader = false
            })
            this.loader = false
            this.isDataFetched = true;
            break
          case 'edit':
            $productFeeds.show(this.id, null).then(response => {
              const productFeed = response.data.data.product_feed
              const categories = response.data.data.categories
              this.skuProducts = response.data.data.sku_products
              // Manage Payload
              this.productFeed = productFeed
              this.getStores();
              if (productFeed.filters.included_categories &&
                productFeed.filters.included_categories.length > 0) {
                this.categoryFilter = 'limit'
                this.categories = categories
              } else if (productFeed.filters.excluded_categories &&
                productFeed.filters.excluded_categories.length > 0) {
                this.categoryFilter = 'exclude'
                this.categories = categories
              }
              this.productFeed.filters.order = productFeed.filters.order || ' '
              this.productFeed.filters.tag_type = productFeed.filters.tag_type
              this.productFeed.filters.product_skus = productFeed.filters.product_skus
              this.dataObj.ids = productFeed.filters.product_skus
              if(productFeed.filters.tag_type === 'product_skus'){
                this.recommendationType = 'skus'
              }else{
                this.recommendationType = 'feeds'
              }
            }).finally(() => {
              this.loader = false
              $crms.fetch().then(response => {
                const results = response.data.data.types
                const sources = []
                results.forEach(source => {
                  sources.push({
                    id: source,
                    label: source.toLowerCase().replace(/_/g, ' ')
                      .replace(/(?: |\b)(\w)/g, (key, p1) => {
                        return key.toUpperCase()
                      }),
                  })
                })
                this.sources = sources
              }).finally(() => {
                this.sourcesLoader = false
              })
              this.loadBrands()
              this.loadCategories()
              this.isDataFetched = true;
            })
            break
          default:
            // default case
            this.categoriesLoader = false
            this.loader = false
        }
      },
      /**
       * @description reset the both (included || excluded) categories filter when action changed
       */
      resetFilter () {
        this.productFeed.filters.included_categories = []
        this.productFeed.filters.excluded_categories = []
      },
      /**
       * @description reset default value in the days range option
       */
      resetDays () {
        if (this.productFeed.metric === 'best_sellers') {
          this.productFeed.filters.day_range = this.metricsBestSellerDays[0].value
        } else {
          this.productFeed.filters.day_range = this.metricsOtherDays[0].value
        }
      },
      /**
       * Handle all type of form submission
       */
      onSubmit () {
        this.touched.store = true;
        setTimeout(()=>{
          // Validate before submitting
          if (this.$refs.feedForm.validate() && this.valid) {
            this.loader = true

            const payload = { 
              product_feed: { 
                ...this.productFeed,
                filters: { 
                  ...this.productFeed.filters,
                  product_skus: this.productFeed.filters.tag_type != 'product_skus' ? [] : this.dataObj.ids
                }
              }
            }
            // If edit submission
            if (this.id) {
              $productFeeds.update(this.id, payload).then(response => {
                this.$root.reloadProductFeeds()
                this.$store.commit('ui/drawer.right.toggle')
                this.$store.commit('ui/snackbar', {
                  message: 'Product feed changes were successfully saved.',
                  type: 'success',
                })
              }).catch((error) => {
                this.errorAlert(error.response.data.errors)
              }).finally(() => {
                this.loader = false
              })
            } else {
              $productFeeds.create(payload).then(response => {
                this.$root.reloadProductFeeds()
                this.$store.commit('ui/drawer.right.toggle')
                this.$store.commit('ui/snackbar', {
                  message: 'Product feed was successfully created.',
                  type: 'success',
                })
              }).catch((error) => {
                this.errorAlert(error.response.data.errors)
              }).finally(() => {
                this.loader = false
              })
            }
          } else {
            this.scrollOnFirstError()
          }
        })
      },
      /**
       * @description used for mange the error bag with key type
       * to inject in UI
       */
      errorAlert (errors) {
        for (const error in errors) {
          this.errorBag[error] = `${error[0].toUpperCase() + error.substring(1).replace(/_/g, ' ')} ${errors[error]}`
        }
        this.scrollOnFirstError()
      },
      /**
       * @description Scroll the view on first validate error
       */
      scrollOnFirstError () {
        this.$nextTick(() => {
          const el = document.querySelector('.v-messages.error--text:first-of-type')
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        })
      },
      /**
       * @description fetch Brands as per source
       * according to cases
       */
      loadBrands () {
        this.brandLoader = true
        this.brands = []
        $products.getAllBrands({
          params: {
            source_type: this.productFeed.filters.source,
            source_id: this.featureFlag ? this.productFeed.filters.store_id : null,
          },
        }).then(response => {
          const results = response.data.data.brands
          this.brands = results
        }).finally(() => {
          this.brandLoader = false
        })
      },
      /**
       * @description to remove already selected brand
       * according to cases
       */
      removeBrand (selectedBrand) {
        const brandIndex = this.productFeed.filters.brands.findIndex(brand => brand === selectedBrand)
        if (brandIndex >= 0) this.productFeed.filters.brands.splice(brandIndex, 1)
      },
      /**
       * @description fetch categories as per source
       * according to cases
       */
      loadCategories () {
        this.categoriesLoader = true
        $categories.fetchHierarchy({
          source_type: this.productFeed.filters.source,
          source_id: this.productFeed.filters.store_id,
          all_record: true,
        }).then(response => {
          this.categories = response.data.categories
        }).finally(() => {
          this.categoriesLoader = false
        })
      },
      /**
       * @description handle the category focus for placeholder fieldset
       */
      onOpenTags () {
        this.categoryFocusFlag = true
      },
      /**
       * @description handle the category onclose for placeholder fieldset
       */
      onCloseTags () {
        this.categoryFocusFlag = (this.productFeed.filters.included_categories.length > 0 ||
          this.productFeed.filters.excluded_categories.length > 0)
      },
      /**
       * @description on source change load respective data in fieldset
       */
      onChangeSource () {
        this.productFeed.filters.brands = []
        this.recommendationType = 'feeds'
        this.resetFilter()
        if(this.productFeed.filters.source === 'default'){
          this.loadBrands()
        }
        this.getStores()
      },
      getStores() {
        this.storesLoader = true
        let payload = { crm_type: this.productFeed.filters.source }
        if(this.featureFlag && this.productFeed.filters.source === 'keap')
          payload.integration_type = 'orders_with_contacts'
        $integrations.getStoresList(payload).then(res => {
          this.stores = res.data
        }).catch(err => { console.log({ err }) }).finally(() => {
          this.storesLoader = false;
        })
      },
      onChangeStore(){
        this.productFeed.filters.brands = []
        this.loadBrands()
        this.loadCategories()
        this.dataObj.ids=[];
        this.productFeed.filters.product_skus=[]
        this.skuProducts = [];
        this.recommendationType = 'feeds'
      },
      getFeeds(value){
        if(value === 'abandoned_carts'){
          $crms.getAbandonedTag(this.productFeed.filters.store_id).then(response=>{
            this.feedList = [{id:this.productFeed.filters.store_id,name:response.data.tag_name}]
          }).catch(error=>{
            console.log({error})
          })
        }else{
          let payload = {metric:value,show_all:true}
          $productFeeds.fetch({params:payload}).then(response=>{
            this.feedList = response.data.data.product_feeds
          }).catch(error=>{
            console.log({error})
          })
        }
      },
      productFeedOptions() {
        const source = this.productFeed.filters.source;
        const options = [
          { value: 'best_sellers', text: 'Best Sellers Feeds' },
          { value: 'new_arrivals', text: 'New Arrivals Feeds' },
          { value: 'trending', text: 'Top Trending Feeds' },
        ];

        // Add "Abandon Cart Feeds" only if the source is neto, clickbank, or shopify
        if (['neto', 'clickbank', 'shopify'].includes(source)) {
          options.unshift({ value: 'abandoned_carts', text: 'Abandon Cart Feeds' });
        }

        return options;
      },
      handleFilterChange(changedField) {
        if (!this.isDataFetched) return;
        const defaultValues = {
          metric: 'best_sellers',
          filters: {
            day_range: 5,
            included_categories: [],
            excluded_categories: [],
            order_by: 'price',
            order: ' ',
            only_published: this.productFeed.filters.only_published,
            only_active: this.productFeed.filters.only_active,
            only_in_stock: this.productFeed.filters.only_in_stock,
            brands: [],
            tag_type: '',
            selected_feed_id: null,
            product_skus: [],
          },
        };

        const exceptions = ['name', 'source'];
        if (changedField === 'store_id') {
          exceptions.push('store_id');
        }
        if (changedField === 'brands') {
          exceptions.push('store_id','brands');
        }

        // Only keep exception fields in `productFeed` and reset others to defaults
        this.productFeed = {
          ...this.productFeed,
          metric: exceptions.includes('metric') ? this.productFeed.metric : defaultValues.metric,
          filters: {
            ...defaultValues.filters,
            ...exceptions.reduce((obj, key) => ({ ...obj, [key]: this.productFeed.filters[key] }), {}),
          },
        };
      },
      getStoreFindifyStatus(id){
        $crms.storeFindifyStatus(id).then(res=>{
          const {enable_free_findify_recommendations,enable_paid_findify_recommendations} = res.data
          this.freeFindifyRecommendations = !enable_free_findify_recommendations
          this.paidFindifyRecommendations = !enable_paid_findify_recommendations
        }).catch(err=>{
          this.freeFindifyRecommendations = true; 
          this.paidFindifyRecommendations = true
          console.log({err})
        })
      },
      initializeSplitClient() {
        try {
            this.featureFlag = getSplitKey(constants.SPLIT_IO_KEYS.findifyRecommendation);  
        } catch (error) {
          console.error('Error initializing Split.io client:', error.message);
        }
      },
      markTouched(field) {
        this.touched[field] = true;
      },
      handleFeedChange(){
        this.productFeed.filters = {
            ...this.productFeed.filters,
            tag_type:'',
            selected_feed_id:null,
            product_skus:[],
          }
          this.dataObj={
          ids:[],
          select_all:false
        }
      }
    },
  }
</script>
<style lang="scss">
#product-feed-drawer {
  .custom-selectbox .v-select__slot label {
    top: 18px;
  }
  .v-input__control {
    width: 100% !important;
  }
  .v-input--selection-controls {
    width: 100% !important;
  }
  .char-count-width {
    max-width: 60ch;
  }
  .categories-fieldset {
    position: absolute;
    background-image: linear-gradient(#ffffff, white);
    color: rgba(0, 0, 0, 0.6);
    font-size: 12px;
    z-index: 1;
  }
}
</style>
